// Google Analytics 4 implementation
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer: any[];
  }
}

const AFFILIATE_CONTEXT_KEY = 'propviper_affiliate_context';

export interface AffiliateClickContext {
  firmId: string;
  firmName?: string;
  sourcePage: string;
  placement: string;
  ctaText: string;
  referrer: string;
  device: 'mobile' | 'tablet' | 'desktop';
  timestamp: string;
}

const getDeviceType = (): AffiliateClickContext['device'] => {
  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
};

const getPlacement = (element: HTMLElement): string =>
  element.dataset.affiliatePlacement ||
  element.closest<HTMLElement>('[data-affiliate-placement]')?.dataset.affiliatePlacement ||
  'unclassified';

const getFirmIdFromHref = (href: string): string | null => {
  try {
    const url = new URL(href, window.location.origin);
    const match = url.pathname.match(/^\/go\/([^/?#]+)/i);
    return match?.[1] || null;
  } catch {
    return null;
  }
};

export const initGA = (measurementId: string) => {
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', measurementId, { send_page_view: false });
};

export const trackPageView = (path: string) => {
  window.gtag?.('event', 'page_view', {
    page_path: path,
    page_title: document.title,
  });
};

export const trackEvent = (eventName: string, eventParams?: Record<string, any>) => {
  window.gtag?.('event', eventName, eventParams);
};

export const saveAffiliateClickContext = (context: AffiliateClickContext) => {
  try {
    sessionStorage.setItem(AFFILIATE_CONTEXT_KEY, JSON.stringify(context));
  } catch {
    // Storage may be blocked; GA tracking should still continue.
  }
};

export const getAffiliateClickContext = (): AffiliateClickContext | null => {
  try {
    const raw = sessionStorage.getItem(AFFILIATE_CONTEXT_KEY);
    return raw ? (JSON.parse(raw) as AffiliateClickContext) : null;
  } catch {
    return null;
  }
};

export const trackAffiliateClick = (
  firmId: string,
  firmName: string,
  context?: Partial<AffiliateClickContext>
) => {
  const storedContext = getAffiliateClickContext();
  const merged = {
    firm_id: firmId,
    firm_name: firmName,
    source_page: context?.sourcePage || storedContext?.sourcePage || window.location.pathname,
    placement: context?.placement || storedContext?.placement || 'redirect',
    cta_text: context?.ctaText || storedContext?.ctaText || 'affiliate_redirect',
    referrer: context?.referrer || storedContext?.referrer || document.referrer || 'direct',
    device: context?.device || storedContext?.device || getDeviceType(),
    timestamp: new Date().toISOString(),
  };

  trackEvent('affiliate_click', merged);
};

export const installAffiliateClickTracking = () => {
  const handleClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement | null;
    const anchor = target?.closest<HTMLAnchorElement>('a[href]');
    if (!anchor) return;

    const firmId = getFirmIdFromHref(anchor.href);
    if (!firmId) return;

    const context: AffiliateClickContext = {
      firmId,
      firmName: anchor.dataset.firmName,
      sourcePage: `${window.location.pathname}${window.location.search}`,
      placement: getPlacement(anchor),
      ctaText: anchor.dataset.affiliateCta || anchor.textContent?.trim() || 'affiliate_link',
      referrer: document.referrer || 'direct',
      device: getDeviceType(),
      timestamp: new Date().toISOString(),
    };

    saveAffiliateClickContext(context);
    trackAffiliateClick(firmId, context.firmName || firmId.toUpperCase(), context);
  };

  document.addEventListener('click', handleClick, { capture: true });
  return () => document.removeEventListener('click', handleClick, { capture: true });
};

export const trackNewsletterSignup = (source: string) => {
  trackEvent('newsletter_signup', { source, timestamp: new Date().toISOString() });
};

export const trackToolUsage = (toolName: string, params?: Record<string, any>) => {
  trackEvent('tool_usage', { tool_name: toolName, ...params, timestamp: new Date().toISOString() });
};

export const trackBlogInteraction = (
  action: 'view' | 'share' | 'comment',
  postSlug: string,
  postTitle: string
) => {
  trackEvent('blog_interaction', {
    action,
    post_slug: postSlug,
    post_title: postTitle,
    timestamp: new Date().toISOString(),
  });
};

export const trackReviewInteraction = (
  action: 'view' | 'compare' | 'share',
  firmSlug: string,
  firmName: string
) => {
  trackEvent('review_interaction', {
    action,
    firm_slug: firmSlug,
    firm_name: firmName,
    timestamp: new Date().toISOString(),
  });
};

export const trackSearch = (query: string, resultsCount: number) => {
  trackEvent('search', { query, results_count: resultsCount, timestamp: new Date().toISOString() });
};

export const trackEngagement = (
  type: 'scroll_depth' | 'time_spent' | 'interaction',
  value: number,
  page: string
) => {
  trackEvent('user_engagement', { type, value, page, timestamp: new Date().toISOString() });
};

export const trackConversion = (
  type: 'signup' | 'purchase' | 'lead',
  value?: number,
  currency?: string
) => {
  trackEvent('conversion', { type, value, currency, timestamp: new Date().toISOString() });
};
